import * as React from 'react'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCallback } from 'react'
import Brightness1RoundedIcon from '@mui/icons-material/Brightness1Rounded'
import {
  StyledText,
  StyledUserNameAndMainColumn,
  StyledTableCell,
} from '../components'
import { useTheme } from '@mui/material/styles'

const UserContractsTableBody = React.memo(({ userGroups }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  const getStatusColor = useCallback(
    (status) => {
      switch (status) {
        case 'ACTIVE':
          return theme.tokens.primary
        case 'INACTIVE':
          return theme.tokens.surfaceContainerHighest
        case 'IN_PROGRESS':
          return theme.status.inPrufung
        default:
          return 'inherit'
      }
    },
    [
      theme.tokens.primary,
      theme.tokens.surfaceContainerHighest,
      theme.status.inPrufung,
    ]
  )

  const handleDetailsClick = useCallback(
    (user, contractDetails) => {
      // navigate(`/nutzer/${user.fullName}/`, {
      //   state: { user, contractDetails },
      // })
      // console.log(contractDetails)
      navigate(
        `/nutzer/${contractDetails.email}/contracts/${contractDetails.uuid}`
      )
    },
    [navigate]
  )

  const handleFullNameClick = useCallback(
    (user) => {
      // navigate(`/nutzer/${user.fullName}/`, { state: { user } })
      navigate(`/nutzer/${user.email}`)
    },
    [navigate]
  )

  const getContractTypeLabel = useCallback(
    (contract) => {
      // Map energyRole to contract type labels
      if (contract.energyRole === 'PRODUCER') {
        return t('LBLProducer')
      } else if (contract.energyRole === 'CONSUMER') {
        return t('LBLConsumer')
      }
    },
    [t]
  )

  return (
    <TableBody>
      {userGroups?.map((user, userIndex) => {
        const isLastUser = userIndex === userGroups.length - 1
        const hasContracts =
          user.contractDataDto && user.contractDataDto.length > 0

        return (
          <React.Fragment key={user.userId}>
            {hasContracts ? (
              user.contractDataDto.map((contract, contractIndex) => {
                const isLastContract =
                  contractIndex === user.contractDataDto.length - 1

                return (
                  <TableRow
                    key={contract.uuid}
                    sx={{
                      borderBottom: isLastContract ? '2px solid' : '1px solid',
                      color: (theme) => theme.tokens.secondary,
                      backgroundColor: 'transparent',
                      marginBottom: isLastContract && !isLastUser ? '4px' : '0',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.tokens.surfaceContainerHighest,
                      },
                    }}
                  >
                    {contractIndex === 0 && (
                      <StyledUserNameAndMainColumn
                        rowSpan={user.contractDataDto.length}
                        style={{
                          borderRight: '1px solid rgba(224, 224, 224, 1)',
                          width: '17%',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                        }}
                      >
                        <Button
                          onClick={() => handleFullNameClick(user)}
                          sx={{ fontWeight: 'bold' }}
                        >
                          <StyledText
                            variant="h5"
                            style={{ textAlign: 'center' }}
                          >
                            {user.fullName === null ? '--' : user.fullName}
                          </StyledText>
                        </Button>
                      </StyledUserNameAndMainColumn>
                    )}
                    <StyledTableCell style={{ width: '14%' }}>
                      <span>
                        {contract?.contractNumber == null
                          ? '--'
                          : contract.contractNumber}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell style={{ width: '12%' }}>
                      {contract.address?.city || '--'}
                    </StyledTableCell>
                    <StyledTableCell style={{ width: '14%' }}>
                      {contract.salutation === 'COMPANY'
                        ? contract.companyName
                        : `${contract.firstName || ''} ${
                            contract.lastName || ''
                          }`.trim() || '--'}
                    </StyledTableCell>
                    <StyledTableCell style={{ width: '12%' }}>
                      {getContractTypeLabel(contract)}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        color: getStatusColor(contract.contractStatus),
                        width: '12%',
                      }}
                    >
                      <Brightness1RoundedIcon
                        sx={{
                          color: getStatusColor(contract.contractStatus),
                          width: 20,
                          height: 20,
                          marginRight: '5px',
                          verticalAlign: 'middle',
                        }}
                      />

                      {contract.contractStatus === 'ACTIVE'
                        ? t('LBLActive')
                        : contract.contractStatus === 'INACTIVE'
                        ? t('LBLInactive')
                        : t('LBLInProgress')}
                    </StyledTableCell>

                    <StyledTableCell style={{ width: '19%' }}>
                      <Button
                        onClick={() => handleDetailsClick(user, contract)}
                        style={{ width: '100%' }}
                      >
                        <StyledText variant="h5">Details</StyledText>
                      </Button>
                    </StyledTableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow
                sx={{
                  borderBottom: !isLastUser ? '2px solid' : 'none',
                  color: (theme) => theme.tokens.secondary,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.tokens.surfaceContainerHighest,
                  },
                }}
              >
                <StyledUserNameAndMainColumn
                  style={{
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    color: (theme) => theme.tokens.secondary,
                    width: '17%',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  <Button
                    onClick={() => handleFullNameClick(user)}
                    sx={{ fontWeight: 'bold' }}
                  >
                    <StyledText variant="h5" style={{ textAlign: 'center' }}>
                      {user.fullName === null ? '--' : user.fullName}
                    </StyledText>
                  </Button>
                </StyledUserNameAndMainColumn>
                <StyledTableCell style={{ width: '14%' }}>
                  {'--'}
                </StyledTableCell>
                <StyledTableCell style={{ width: '12%' }}>
                  {'--'}
                </StyledTableCell>
                <StyledTableCell style={{ width: '14%' }}>
                  {'--'}
                </StyledTableCell>
                <StyledTableCell style={{ width: '12%' }}>
                  {'--'}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    color: getStatusColor('INACTIVE'),
                    width: '12%',
                  }}
                >
                  {'--'}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    width: '19%',
                  }}
                >
                  <Button onClick={() => handleDetailsClick(user, null)}>
                    <StyledText variant="h5">--</StyledText>
                  </Button>
                </StyledTableCell>
              </TableRow>
            )}
          </React.Fragment>
        )
      })}
    </TableBody>
  )
})

UserContractsTableBody.displayName = 'UserContractsTableBody'

export default UserContractsTableBody
